import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import PermissionGuard from '../../components/common/PermissionGuard';

const CompanySettings = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [company, setCompany] = useState({
        name: '',
        address: '',
        city: '',
        zipCode: '',
        phone: '',
        email: '',
        siret: '',
        vatNumber: '',
        website: '',
        description: '',
        logo: '',
        lat: null,
        lng: null
    });

    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceRef = useRef(null);

    const fetchCompany = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const res = await fetch(`/api/companies/${user.companyId}`);
            if (res.ok) {
                const data = await res.json();
                setCompany(prev => ({ ...prev, ...data }));
            }
        } catch (error) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, showToast]);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    const handleAddressSearch = (value) => {
        setCompany(prev => ({ ...prev, address: value }));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.length < 3) { setAddressSuggestions([]); return; }

        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5&addressdetails=1`);
                const data = await res.json();
                setAddressSuggestions(data);
                setShowSuggestions(true);
            } catch (e) {
                console.error(e);
            }
        }, 400);
    };

    const selectAddress = (suggestion) => {
        const addr = suggestion.address;

        // Combine house number and road if available, else fallback to first part of display_name
        const streetAddress = (addr.house_number && addr.road)
            ? `${addr.house_number} ${addr.road}`
            : (addr.road || suggestion.display_name.split(',')[0]);

        setCompany(prev => ({
            ...prev,
            address: streetAddress,
            city: addr.city || addr.town || addr.village || '',
            zipCode: addr.postcode || '',
            lat: parseFloat(suggestion.lat),
            lng: parseFloat(suggestion.lng || suggestion.lon)
        }));
        setShowSuggestions(false);
        setAddressSuggestions([]);
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Optional: check file size (e.g., limit to 2MB)
            if (file.size > 2 * 1024 * 1024) {
                showToast("Image trop volumineuse (max 2MB)", "error");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompany(prev => ({ ...prev, logo: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch(`/api/companies/${user.companyId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(company)
            });

            if (res.ok) {
                showToast("Informations mises à jour avec succès", "success");
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur lors de la mise à jour", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <PermissionGuard permission="settings.manage">
            <div className="flex flex-col bg-background-light dark:bg-background-dark -m-6 lg:-m-10">
                {/* 1. Header Sticky Section */}
                <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50 transition-all duration-200">
                    <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full px-6">
                        {/* Titre & Sous-titre */}
                        <div className="flex flex-col gap-1">
                            <h1 className="text-3xl font-black leading-tight tracking-tight flex items-center gap-3 bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">
                                <span className="material-symbols-outlined text-primary text-3xl">domain</span>
                                Ma Société
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gérez l'identité et les coordonnées de votre entreprise</p>
                        </div>

                        {/* Actions du Header */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="size-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-background-light transition-all active:scale-95 shadow-sm"
                                title="Rafraîchir"
                            >
                                <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">refresh</span>
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="h-10 px-6 flex items-center gap-2 bg-primary text-white font-bold rounded-lg shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70"
                            >
                                {saving ? (
                                    <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <span className="material-symbols-outlined text-sm font-bold">save</span>
                                )}
                                <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. Contenu Principal */}
                <main className="flex-1 p-6 lg:p-8 max-w-[1200px] mx-auto w-full space-y-8 animate-fade-in">
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Section: Identité */}
                        <div className="bg-white dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-xl shadow-slate-200/50 dark:shadow-none transition-all hover:border-primary/20">
                            <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-background-light/50 dark:bg-background-dark/30 flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary text-xl">info</span>
                                <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Identité Légale</h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="flex flex-col md:flex-row gap-6 items-center bg-slate-50 dark:bg-slate-800/20 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/50">
                                    <div className="relative size-24 shrink-0 rounded-2xl bg-white dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center overflow-hidden group cursor-pointer hover:border-primary/50 transition-colors">
                                        {company.logo ? (
                                            <img src={company.logo} alt="Logo" className="w-full h-full object-contain p-2" />
                                        ) : (
                                            <span className="material-symbols-outlined text-3xl text-slate-400 group-hover:text-primary transition-colors">add_photo_alternate</span>
                                        )}
                                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity backdrop-blur-[2px]">
                                            <span className="material-symbols-outlined text-white mb-1">upload</span>
                                            <span className="text-[8px] font-bold text-white uppercase tracking-wider">Modifier</span>
                                        </div>
                                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" title="Changer le logo" />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Logo de l'entreprise</h3>
                                        <p className="text-xs text-slate-500 mt-1 max-w-sm">Ce logo sera utilisé en en-tête de vos PDF (bons de commande, etc.).<br /><span className="italic">Format recommandé: PNG/JPG, fond transparent. Max 2MB.</span></p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Désignation sociale</label>
                                        <input
                                            required
                                            className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                            value={company.name}
                                            onChange={e => setCompany({ ...company, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Numéro SIRET</label>
                                        <input
                                            className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-mono outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                            value={company.siret}
                                            placeholder="123 456 789 00012"
                                            onChange={e => setCompany({ ...company, siret: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">N° TVA Intracommunautaire</label>
                                        <input
                                            className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-mono outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                            value={company.vatNumber}
                                            placeholder="FR 12 345678901"
                                            onChange={e => setCompany({ ...company, vatNumber: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section: Localisation */}
                        <div className="bg-white dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-xl shadow-slate-200/50 dark:shadow-none transition-all hover:border-primary/20">
                            <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-background-light/50 dark:bg-background-dark/30 flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary text-xl">location_on</span>
                                <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Siège Social & GPS</h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="space-y-4">
                                    <div className="relative">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1 block mb-2">Recherche d'adresse (OSM)</label>
                                        <div className="relative group">
                                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
                                            <input
                                                className="w-full p-4 pl-10 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.address}
                                                onChange={e => handleAddressSearch(e.target.value)}
                                                onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                                placeholder="Tapez pour rechercher..."
                                            />
                                        </div>
                                        {showSuggestions && addressSuggestions.length > 0 && (
                                            <div className="absolute z-[40] w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl max-h-64 overflow-y-auto backdrop-blur-md">
                                                {addressSuggestions.map((s, idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => selectAddress(s)}
                                                        className="w-full text-left p-4 hover:bg-background-light/50 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <span className="material-symbols-outlined text-primary text-lg">location_on</span>
                                                            <p className="text-xs font-bold text-slate-600 dark:text-slate-300 line-clamp-2">{s.display_name}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-xs">
                                        <div className="p-3 bg-background-light/30 dark:bg-slate-800/30 rounded-lg flex items-center justify-between">
                                            <span className="text-slate-400 font-bold uppercase text-[9px]">Lat:</span>
                                            <span className="font-mono text-primary">{company.lat?.toFixed(6) || 'N/A'}</span>
                                        </div>
                                        <div className="p-3 bg-background-light/30 dark:bg-slate-800/30 rounded-lg flex items-center justify-between">
                                            <span className="text-slate-400 font-bold uppercase text-[9px]">Lng:</span>
                                            <span className="font-mono text-primary">{company.lng?.toFixed(6) || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Ville</label>
                                            <input
                                                className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.city}
                                                onChange={e => setCompany({ ...company, city: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Code Postal</label>
                                            <input
                                                className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-mono font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.zipCode}
                                                onChange={e => setCompany({ ...company, zipCode: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section: Contact */}
                        <div className="bg-white dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-xl shadow-slate-200/50 dark:shadow-none lg:col-span-2 transition-all hover:border-primary/20">
                            <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-background-light/50 dark:bg-background-dark/30 flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary text-xl">contact_support</span>
                                <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Coordonnées & Web</h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Email professionnel</label>
                                        <div className="relative">
                                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">mail</span>
                                            <input
                                                type="email"
                                                className="w-full p-4 pl-10 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.email}
                                                onChange={e => setCompany({ ...company, email: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Téléphone</label>
                                        <div className="relative">
                                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">phone</span>
                                            <input
                                                type="tel"
                                                className="w-full p-4 pl-10 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.phone}
                                                onChange={e => setCompany({ ...company, phone: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Site internet</label>
                                        <div className="relative">
                                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">language</span>
                                            <input
                                                className="w-full p-4 pl-10 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all"
                                                value={company.website}
                                                placeholder="https://..."
                                                onChange={e => setCompany({ ...company, website: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 space-y-2">
                                    <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Description sommaire</label>
                                    <textarea
                                        className="w-full p-4 bg-background-light/50 dark:bg-slate-800/50 border-none rounded-xl text-sm outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all min-h-[80px]"
                                        value={company.description}
                                        onChange={e => setCompany({ ...company, description: e.target.value })}
                                        placeholder="Activités principales de l'entreprise..."
                                    />
                                </div>
                            </div>
                        </div>


                    </form>
                </main>
            </div>
        </PermissionGuard>
    );
};

export default CompanySettings;
